import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, first, iif, map, mergeMap, Observable, of, switchMap, tap, zip } from 'rxjs';
import { UserProxyService } from 'src/app/infrastructure/lobby-proxy/lobby-proxy-user/user-proxy.service';
import { UserAction } from '../../user/action-types';
import { userId, userProperties, userVersion } from '../../user/user.selectors';
import { BoardColumnStructure } from '../models/board-column-structure.model';
import { BoardStructure } from '../models/board-structure.model';
import { BoardStructureProperty } from '../models/board-structure-property.model';

@Injectable({
  providedIn: 'root'
})
export class BoardStructureService {

  private _currentUserVersion$: Observable<number>
  private _currentUserId$: Observable<number>

  constructor(private readonly appStore: Store,
    private readonly httpClient: HttpClient,
    private readonly userProxyService: UserProxyService) {
    this._currentUserId$ = appStore.pipe(userId)
    this._currentUserVersion$ = appStore.pipe(userVersion)
  }



  getBoardStructure$(boardName: string): Observable<BoardStructure> {
    return zip(
      this.getDefaultBoardStructure$(boardName),
      this.getSavedBoardStructure$(boardName)
    ).pipe(
      mergeMap(([defaultStructure, savedStructure]) =>
        iif(() => (!savedStructure || (defaultStructure?.version ?? -1) > savedStructure?.version),
          this.saveBoardStructure$(defaultStructure, !!!savedStructure)
            .pipe(
              tap(() => this.appStore.dispatch(UserAction.loadUser())),
              map(() => defaultStructure)
            ),
          of(savedStructure).pipe(
            map(structure => {
              return {
                name: boardName,
                version: structure!.version,
                columns: structure?.columns.map(c => defaultStructure!.columns.find(sc => sc.name == c.name)!.mergeWithProperty(c))
              } as BoardStructure
            }),
            catchError(() => this.saveBoardStructure$(defaultStructure)
              .pipe(
                tap(() => this.appStore.dispatch(UserAction.loadUser())),
                map(() => defaultStructure)
              )
            )
          ))
      )
    )
  }

  getDefaultBoardStructure$(boardName: string): Observable<BoardStructure> {
    return this.httpClient.get<BoardStructure[]>('/assets/configurations/board-structure.config.json').pipe(
      map(structures => structures.find(s => s.name == boardName)),
      //tap(x => console.log('board structure', x, boardName)),
      map(structure => {
        return {
          name: structure!.name,
          version: structure!.version,
          columns: structure!.columns.map(c => BoardColumnStructure.fromConfiguration(c))
        }
      })
    )
  }

  saveBoardStructure$(structure: BoardStructure, isNewProperty: boolean = false) {

    const propertyStructure: BoardStructureProperty = {
      version: structure.version,
      columns: structure.columns.map(c => { return { name: c.name, isSelected: c.isSelected } })
    }

    return zip(this._currentUserId$, this._currentUserVersion$).pipe(
      first(),
      switchMap(([userId, version]) => isNewProperty ?
        this.userProxyService.setNewUserProperty$(userId, version, this.getPropertyKeyName(structure.name), JSON.stringify(propertyStructure)) :
        this.userProxyService.setUserProperty$(userId, version, this.getPropertyKeyName(structure.name), JSON.stringify(propertyStructure))
      ),
      tap(() => this.appStore.dispatch(UserAction.loadUser()))
    )
  }

  private getSavedBoardStructure$(boardName: string): Observable<BoardStructureProperty | undefined> {
    return this.appStore.pipe(userProperties).pipe(
      map(properties => properties?.find(p => p.name == this.getPropertyKeyName(boardName))),
      map(property => property ? (JSON.parse(property?.value as string) as BoardStructureProperty | undefined) : undefined)
    )
  }

  private getPropertyKeyName(boardName: string) {
    return 'Webapp_' + boardName + 'Board'
  }
}
